import type { SuggestedCandidate, APIStatus } from '../types/SuggestedCandidate';
import { API_STATUS_LABELS } from '../types/SuggestedCandidate';
import { useState } from 'react';
import { Button } from '@/components/Button';
import { Tag, type TagColor } from '@/components/Tag';
import SuggestedCandidateCommentModal from './SuggestedCandidateCommentModal';
import SuggestedCandidateStatusChangeModal from './SuggestedCandidateStatusChangeModal';
import type { StatusChangeType } from './SuggestedCandidateStatusChangeModal';

type Props = {
  candidate: SuggestedCandidate;
  jobPostId: number;
};

export default function SuggestedCandidateListItem({ candidate, jobPostId }: Props) {
  const { candidate_profile } = candidate;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusChangeType, setStatusChangeType] = useState<StatusChangeType>('toSuggest');
  const [comment, setComment] = useState(candidate.comment || '');
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);

  const openModal = (type: StatusChangeType) => {
    setStatusChangeType(type);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const getStatusTagColor = (status: APIStatus): TagColor => {
    switch (status) {
      case 'suggest':
        return 'blue';
      case 'apply':
        return 'green';
      case 'decline':
        return 'red';
      case 'archived':
        return 'gray';
      case 'matched':
        return 'purple';
      default:
        return 'gray';
    }
  };

  return (
    <div className="grid grid-cols-[2fr_1fr_2fr_1.5fr] gap-4 rounded bg-white p-4 shadow-[0px_0px_6px_0px_#00000033]">
      <div className="flex items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold">
            {candidate_profile.name}
            {candidate_profile.age && ` (${candidate_profile.age}歳)`}
          </span>
          {candidate_profile.organization_users?.[0] && (
            <Tag color="default" size="lg">
              {candidate_profile.organization_users[0].name}
            </Tag>
          )}
        </div>
      </div>
      <div className="flex items-center justify-center">
        <Tag color={getStatusTagColor(candidate.status)} size="lg">
          {API_STATUS_LABELS[candidate.status]}
        </Tag>
      </div>

      {/* エージェントからのコメント */}
      <div className="flex items-center justify-center">
        <div
          role="button"
          tabIndex={0}
          className="h-[72px] w-full cursor-pointer overflow-auto rounded border border-solid border-border bg-white p-2 text-xs leading-[170%] break-words whitespace-pre-wrap"
          onClick={() => setIsCommentModalOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsCommentModalOpen(true);
            }
          }}
        >
          {comment || '-'}
        </div>
      </div>

      {/* アクション */}
      <div className="flex items-center justify-center">
        <div className="flex gap-3">
          {(candidate.status === 'suggest' || candidate.status === 'apply') && (
            <Button
              type="button"
              color="danger-secondary"
              size="sm"
              onClick={() => openModal('toWithdraw')}
            >
              取り下げ
            </Button>
          )}

          {(candidate.status === 'decline' || candidate.status === 'archived') && (
            <Button
              type="button"
              color="secondary"
              size="sm"
              onClick={() => openModal('toSuggest')}
            >
              提案へ戻す
            </Button>
          )}

          {/* 取り下げ済みのみ物理削除可能 */}
          {candidate.status === 'archived' && (
            <Button
              type="button"
              color="danger-secondary"
              size="sm"
              onClick={() => openModal('toDelete')}
            >
              削除
            </Button>
          )}

          {(candidate.status === 'suggest' || candidate.status === 'apply') && (
            <Button
              type="button"
              color="primary"
              size="sm"
              onClick={() => openModal('toSelection')}
            >
              選考へ進む
            </Button>
          )}
        </div>
      </div>

      <SuggestedCandidateCommentModal
        modal={{
          id: `suggested-candidate-comment-modal-${candidate.id}`,
          isShow: isCommentModalOpen,
        }}
        close={() => setIsCommentModalOpen(false)}
        title={`${candidate_profile.name}${candidate_profile.age ? ` (${candidate_profile.age}歳)` : ''}`}
        initialComment={comment}
        jobPostId={jobPostId}
        candidateId={candidate.id}
        onSaved={setComment}
      />

      <SuggestedCandidateStatusChangeModal
        modal={{
          id: `suggested-candidate-status-change-modal-${candidate.id}`,
          isShow: isModalOpen,
        }}
        close={closeModal}
        statusChangeType={statusChangeType}
        providedJobPostId={jobPostId}
        candidateIds={[candidate.id]}
      />
    </div>
  );
}
